import { useState, SetStateAction, Dispatch } from "react";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";

interface UseFilesTabDialogsReturn {
  // Folder creation dialog
  isCreateFolderDialogOpen: boolean;
  setIsCreateFolderDialogOpen: Dispatch<SetStateAction<boolean>>;

  // Document edit dialog
  openEditDocumentDialog: Item | null;
  setOpenEditDocumentDialog: Dispatch<SetStateAction<Item | null>>;

  // SharePoint sync dialog
  isSyncingToSharePoint: boolean;
  setIsSyncingToSharePoint: Dispatch<SetStateAction<boolean>>;
  syncDocument: WorkspaceDocument | null;
  setSyncDocument: Dispatch<SetStateAction<WorkspaceDocument | null>>;

  // Word information dialog
  wordInformationDialogOpen: boolean;
  setWordInformationDialogOpen: Dispatch<SetStateAction<boolean>>;
  wordInformationItem: Item | null;
  setWordInformationItem: Dispatch<SetStateAction<Item | null>>;

  // Folder rename dialog
  renameFolderItem: Item | null;
  setRenameFolderItem: Dispatch<SetStateAction<Item | null>>;

  // Document rename dialog
  renameDocumentItem: Item | null;
  setRenameDocumentItem: Dispatch<SetStateAction<Item | null>>;

  // Link edit dialog (LAR-415)
  editLinkItem: Item | null;
  setEditLinkItem: Dispatch<SetStateAction<Item | null>>;
}

/**
 * Hook for managing dialog state in FilesTab
 *
 * Centralizes all dialog state management:
 * - Folder creation dialog
 * - Folder rename dialog
 * - Document rename dialog
 * - Document edit/metadata dialog
 * - SharePoint sync dialog
 *
 * This hook provides a single source of truth for all dialog states,
 * making it easier to manage dialog lifecycle and prevent state conflicts.
 */
export const useFilesTabDialogs = (): UseFilesTabDialogsReturn => {
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [openEditDocumentDialog, setOpenEditDocumentDialog] =
    useState<Item | null>(null);
  const [isSyncingToSharePoint, setIsSyncingToSharePoint] = useState(false);
  const [syncDocument, setSyncDocument] = useState<WorkspaceDocument | null>(
    null
  );
  const [wordInformationItem, setWordInformationItem] = useState<Item | null>(
    null
  );
  const [wordInformationDialogOpen, setWordInformationDialogOpen] =
    useState(false);
  const [renameFolderItem, setRenameFolderItem] = useState<Item | null>(null);
  const [renameDocumentItem, setRenameDocumentItem] = useState<Item | null>(
    null
  );
  const [editLinkItem, setEditLinkItem] = useState<Item | null>(null);
  return {
    isCreateFolderDialogOpen,
    setIsCreateFolderDialogOpen,
    openEditDocumentDialog,
    setOpenEditDocumentDialog,
    isSyncingToSharePoint,
    setIsSyncingToSharePoint,
    syncDocument,
    setSyncDocument,
    wordInformationDialogOpen,
    setWordInformationDialogOpen,
    wordInformationItem,
    setWordInformationItem,
    renameFolderItem,
    setRenameFolderItem,
    renameDocumentItem,
    setRenameDocumentItem,
    editLinkItem,
    setEditLinkItem,
  };
};
